/*!
 * sources.js — a synth's sounds that are not an oscillator or a noise buffer
 * © 2026 Rob Canning — GPLv3
 *
 *     synth(uid:d, wave:dust, freq:40)            clicks, about freq a second (noiseGenCore.js)
 *     synth(uid:s, wave:step, freq:300)           stepped noise: a new level freq times a second
 *     synth(uid:them, wave:in, fuzz:0.8, crush:4) the external audio input, through the voice
 *
 * Each is given what synth.js asks of a source -- start(t), stop(t), a
 * frequency param, onended -- so the rest of the voice (envelope, fuzz,
 * grit, filter, delay, reverb, its scope, gate, level) is as for any sound.
 */

import { loadWorklet } from "./workletFx.js";

const URL_ = new URL("./noiseGenWorklet.js", import.meta.url).href;
let ready = false;

/** Load the generators into this context's engine (once). */
export function loadNoiseGen(ctx) {
  const p = loadWorklet(ctx, URL_);
  p.then(() => { ready = true; }).catch(() => { /* white noise instead */ });
  return p;
}

export const GEN_WAVES = new Set(["dust", "step"]);
export const INPUT_WAVES = new Set(["in", "input", "adc"]);

/** stop(t): at t, its end -- disconnected, onended called (as a source node's). */
function endAt(ctx, node, t, also = () => {}) {
  const ms = Math.max(0, ((t ?? ctx.currentTime) - ctx.currentTime) * 1000) + 30;
  setTimeout(() => { also(); try { node.disconnect(); } catch { /* gone */ } node.onended?.(); }, ms);
}

/** wave:dust / wave:step at `hz`; null until the engine has the generator (the caller plays white noise). */
export function genSource(ctx, mode, hz) {
  if (!ready) return null;
  const node = new AudioWorkletNode(ctx, "oscilla-noisegen", {
    numberOfInputs: 0, numberOfOutputs: 1, outputChannelCount: [1], processorOptions: { mode },
  });
  node.frequency = node.parameters.get("frequency");
  node.frequency.value = Math.max(0, Number(hz) || 0);
  node.start = () => {};
  node.stop = t => { node.port.postMessage({ stopAt: t ?? ctx.currentTime }); endAt(ctx, node, t); };
  return node;
}

/**
 * wave:in: the input (the one adc() hears), into the voice. A gain at once;
 * the input joins it when the browser has opened it (asked first). Said in
 * the console when it cannot be. Its frequency is a stand-in: the input has
 * no pitch to set.
 */
export function inputSource(ctx, say = () => {}) {
  const node = ctx.createGain();
  node.frequency = ctx.createConstantSource().offset;
  let src = null, ended = false, analysis = null;
  import("../../control/audioAnalysis.js").then(async A => {
    analysis = A;
    src = await A.inputSourceNode();
    if (ended) { if (src) A.releaseInputSource(); return; }    // over before the input opened
    if (src) src.connect(node);
    else say(`wave:in -- ${A.inputTrouble() ?? "the input did not open"}`);
  });
  node.start = () => {};
  // the input given back when the voice ends: closed when nothing else uses it
  node.stop = t => endAt(ctx, node, t, () => {
    ended = true;
    if (!src) return;
    const joined = src;
    src = null;                  // given back once, however often it is stopped
    try { joined.disconnect(node); } catch { /* not joined */ }
    analysis.releaseInputSource();
  });
  return node;
}
